import { debounce } from "lodash";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import { Grid, Typography } from "@mui/material";
import { useState, useEffect, Fragment } from "react";

import {
  TextField,
  SelectField,
  Autocomplete,
  LoadingButton,
  MandatoryNote,
} from "../../../../components";
import { useAxios, useAlert } from "../../../../hooks";

// ---- styled TextField ----
const TextFieldWithCSS = styled(TextField)`
  .MuiOutlinedInput-notchedOutline {
    border: 1px solid rgb(0 0 0 / 37%);
    border-radius: 12.5px;
  }
  .MuiOutlinedInput-root {
    font-size: 15px;
  }
  & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(0, 0, 0, 0.1);
  }
  & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(0, 0, 0, 0.1);
  }
  .MuiInputLabel-root {
    color: rgba(0, 0, 0, 0.88);
  }
  .Mui-disabled {
    -webkit-text-fill-color: rgba(0, 0, 0, 0.7);
    background-color: #f5f5f5;
    font-size: 15px;
    border-radius: 12.5px;
  }
  .Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: #f5f5f5;
  }
  .MuiInputLabel-asterisk {
    color: ${(props) => (Boolean(props.required && !props.value) ? "red" : "")};
  }
`;

// ---- Combined Form ----
function IndependentAppointmentFormFields({
  loading,
  premisesID,
  formikProps,
  officerOptions,
  setCustomFields,
  setOfficerOptions,
}) {
  const axios = useAxios();

  const { alert } = useAlert();
  const dispatch = useDispatch();

  // const [officerOptions, setOfficerOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const handleOfficerChange = (value, formikProps) => {
    const selectedOfficer = value ? value.id : "";

    if (selectedOfficer) {
      const officerData = officerOptions.find((o) => o._id === selectedOfficer);
      if (!officerData) return;

      axios({
        url: `admin/custom-fields/get-appointment-fields?business_id=${officerData.business_id}`,
        disableError: true,
        disableRedirect: true,
      }).then((response) => {
        if (response.status) {
          setCustomFields(response.data);
        }
      });

      formikProps.setFieldValue("officer", selectedOfficer);
    }
  };

  // useEffect(() => {
  //   if (premisesID) {
  //     axios({
  //       url: `/appointment/appointment/get-premises-officers?premises_id=${premisesID}`,
  //       disableError: true,
  //       disableRedirect: true,
  //     }).then((response) => {
  //       if (response.status) {
  //         setOfficerOptions(response.data);
  //         const officerData = response.data[0];
  //         if (!officerData) return;

  //         axios({
  //           url: `admin/custom-fields/get-appointment-fields?business_id=${officerData.business_id}`,
  //           disableError: true,
  //           disableRedirect: true,
  //         }).then((response) => {
  //           if (response.status) {
  //             setCustomFields(response.data);
  //           }
  //         });

  //         formikProps.setFieldValue("officer", response.data[0]?._id);
  //       }
  //     });
  //   }
  // }, [premisesID]);

  const fetchOfficers = async (premisesId, searchKey = "") => {
    if (!premisesId) return;
    axios({
      url: `/appointment/appointment/get-premises-officers?premises_id=${premisesId}&search=${searchKey}`,
      disableError: true,
      disableRedirect: true,
    }).then((response) => {
      if (response?.status) setOfficerOptions(response.data);
    });
  };

  const debouncedFetchOfficers = debounce((premisesId, searchKey) => {
    fetchOfficers(premisesId, searchKey);
  }, 500);

  return (
    <Fragment>
      <Grid container spacing={4} sx={{ marginTop: "17.5px" }}>
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
            Your name <span style={{ color: "red" }}>*</span>
          </Typography>

          <TextField name="name" formikProps={formikProps} />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
            Who are you visiting ? <span style={{ color: "red" }}>*</span>
          </Typography>

          <Autocomplete
            name="officer"
            formikProps={formikProps}
            // placeholder="Select a host"
            defaults={{
              primaryKey: "id",
              displayLabel: "name",
            }}
            onInputChange={(_, value, reason) => {
              if (reason === "clear" || reason === "reset") {
                setSearchInput("");
                formikProps.setFieldValue("officer", "");
                fetchOfficers(formikProps.values.premises, "");
              } else {
                setSearchInput(value);
                debouncedFetchOfficers(formikProps.values.premises, value);
              }
            }}
            options={officerOptions.map((o) => ({
              id: o._id,
              name: `${o.name} - ${o.department_name}`,
            }))}
            onChange={(_, value) => {
              handleOfficerChange(value, formikProps);
            }}
            onOpen={() => {
              if (officerOptions.length === 0)
                fetchOfficers(formikProps.values.premises);
            }}
            error={
              formikProps.touched.officer && Boolean(formikProps.errors.officer)
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: "900", mb: 1.5 }}>
            Purpose of visit <span style={{ color: "red" }}>*</span>
          </Typography>

          <SelectField
            // label={"Purpose Visit"}
            name="PurposeVisit"
            placeHolder="Purpose Visit"
            value={formikProps.values.PurposeVisit}
            options={[
              { id: "official", name: "Official" },
              { id: "personal", name: "Personal" },
              { id: "other", name: "Other" },
            ]}
            onChange={formikProps.handleChange}
            onBlur={formikProps.handleBlur}
            error={
              formikProps.touched.PurposeVisit &&
              Boolean(formikProps.errors.PurposeVisit)
            }
            xs={12}
          />
        </Grid>

        {formikProps.values.PurposeVisit == "other" && (
          <Grid item xs={12}>
            <TextFieldWithCSS
              fullWidth
              placeholder="Please fill in the reason for your appointment"
              name="reason"
              value={formikProps.values.reason}
              onChange={(e) => {
                if (e.target.value.length <= 250) {
                  formikProps.handleChange(e);
                }
              }}
              onBlur={formikProps.handleBlur}
              error={
                formikProps.touched.reason && Boolean(formikProps.errors.reason)
              }
              multiline
              rows={6}
              helperText={`${formikProps.values.reason.length} / 250 characters`}
            />
          </Grid>
        )}
      </Grid>

      <Fragment>
        <MandatoryNote />

        <LoadingButton
          disabled={!formikProps.dirty || !formikProps.isValid}
          onClick={formikProps.handleSubmit}
          loading={loading}
          style={{ marginTop: "10px" }}
        >
          Save & Proceed
        </LoadingButton>
      </Fragment>
    </Fragment>
  );
}

export default IndependentAppointmentFormFields;
